import { PayloadAction } from '@reduxjs/toolkit';
import {
  Address,
  Appointment,
  AppointmentConcern,
  AppointmentSlot,
  BookingFlowOption,
} from 'generated/api';
import { createResettableSlice } from 'careand-redux/utils/makeResettable';
import moment from 'moment-timezone';

export interface BookingState {
  option?: BookingFlowOption;
  concern?: AppointmentConcern;
  deliveryOptionIndex?: number;
  appointment: Partial<Appointment>;
  mustShowWarningMessage: boolean;
  bookedAppointmentId?: string;
}

const initialState: BookingState = {
  appointment: {
    complaints: '',
    notes: '',
  },
  mustShowWarningMessage: true,
};

const slice = createResettableSlice({
  name: 'Booking',
  initialState,
  reducers: {
    bookingSuccess: (state, action: PayloadAction<string | undefined>) => {
      return {
        ...initialState,
        appointment: {
          ...initialState.appointment,
          patient_id: state.appointment?.patient_id,
        },
        mustShowWarningMessage: state.mustShowWarningMessage,
        bookedAppointmentId: action.payload,
      };
    },
    reset: (state) => {
      return { ...initialState, mustShowWarningMessage: state.mustShowWarningMessage };
    },
    bookingProfileChanged: (
      state,
      action: PayloadAction<{ patientId: string; clinicId?: string }>,
    ) => {
      state.appointment = {
        patient_id: action.payload.patientId,
        clinic_id: action.payload.clinicId,
      };
    },
    bookingFlowOptionChanged: (state, action: PayloadAction<BookingFlowOption>) => {
      state.option = action.payload;
    },
    warningMessageWasShown: (state) => {
      state.mustShowWarningMessage = false;
    },
    bookingFlowConcernChanged: (state, action: PayloadAction<AppointmentConcern>) => {
      const concern = action.payload;
      state.concern = concern;
      state.deliveryOptionIndex = undefined;
      if (concern.delivery_options?.length === 1) {
        // Auto-select single delivery option
        state.deliveryOptionIndex = 0;
        state.appointment = {
          ...state.appointment,
          ...concern.delivery_options[0],
          concern_id: concern.id,
          delivery_option_index: 0,
        };
      } else if (!concern.delivery_options?.length && concern.appointment_data) {
        // Legacy fallback
        state.appointment = {
          ...state.appointment,
          ...concern.appointment_data,
          concern_id: concern.id,
        };
      } else {
        // Multiple options — wait for delivery option selection
        state.appointment = {
          ...state.appointment,
          concern_id: concern.id,
        };
      }
    },
    deliveryOptionChanged: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      const option = state.concern?.delivery_options?.[index];
      if (option) {
        state.deliveryOptionIndex = index;
        state.appointment = {
          ...state.appointment,
          ...option,
          concern_id: state.concern?.id,
          delivery_option_index: index,
        };
      }
    },
    reasonChanged: (state, action: PayloadAction<string>) => {
      state.appointment.complaints = action.payload;
      state.appointment.date = moment().unix();
    },
    homeAppointmentNotesChanged: (
      state,
      action: PayloadAction<{ address: Address; notes: string }>,
    ) => {
      state.appointment.notes = action.payload.notes;
      state.appointment.address = action.payload.address;
    },
    clinicAndPractitionerChanged: (
      state,
      action: PayloadAction<{ clinicId: string; physicianId?: string }>,
    ) => {
      state.appointment.clinic_id = action.payload.clinicId;
      state.appointment.physician_id = action.payload.physicianId;
    },
    timeSlotSelected: (
      state,
      action: PayloadAction<{ slot: AppointmentSlot; physicianId: string }>,
    ) => {
      state.appointment.date = action.payload.slot.start;
      state.appointment.physician_id = action.payload.physicianId;
    },
  },
});

export const bookingActions = slice.actions;
export default slice.reducer;
